import { QueryKey } from "@/api";
import { Api } from "@/api/Api";
import {
  type AsyncResult,
  createNotRequestedAsyncResult,
  createPendingAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import { hasTaCodePoints } from "@/cache/CustomGeoJSONCache/hasTaCodePoints";
import { hydratePointFeatures } from "@/cache/CustomGeoJSONCache/hydratePointFeatures";
import { createPointByPolygonProperty, processCustomGeoJson } from "@/cache/CustomGeoJSONCache/processCustomGeoJson";
import type {
  CustomGeoJSONAnimationFrameContext,
  CustomGeoJSONPolygonFeatureState,
  CustomGeoJSONPolygonSeriesChunk,
  SlicedCustomGeoJSON,
} from "@/cache/CustomGeoJSONCache/types";
import {
  advancedThresholdValuePropertyName,
  constructHashId,
  constructPolygonSeriesChunkHashId,
} from "@/cache/CustomGeoJSONCache/utils";
import { type CacheDatum, CacheDatumState, type LoadedCacheDatum, networkCaches } from "@/cache/GlobalCache";
import { FailureState, type RetryState, createRetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "@/cache/utils/cacheEviction";
import { createLoadedCacheDatum, sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import { customGeoJsonLayerConfig } from "@/constants/layerConfigAttributes";
import { isGeojsonBackendHydrationEnabled } from "@/feature-flags/non-hook/non-hook";
import { emptyFeatureCollection } from "@/geojson";
import type { CustomGeoJsonRequestOption } from "@/layers/geojson/custom";
import {
  recordCustomGeojsonCounts,
  recordCustomGeojsonHydrateMs,
  recordCustomGeojsonTotalMs,
  recordLayerFetchMs,
} from "@/otel/metrics";
import { createPerfRun } from "@/perf-log/perf-log";
import { dataProcessingThreadPool } from "@/threads/DataProcessingThread/DataProcessingThreadPool";
import { MiB } from "@/utility/bytesize";
import { enumValues, toEnumValue } from "@/utility/enum";
import { Aggregate, type CoordinateSystem, type PolygonRequest } from "@mm/api.meteomatics.com";
import type { HydrationParameterIn } from "@mm/metx-workbench.meteomatics.com";
import type {
  Feature,
  FeatureCollection,
  GeoJSON,
  GeoJsonProperties,
  LineString,
  Point,
  Polygon as PolygonGeometry,
} from "geojson";
import { DateTime, Duration } from "luxon";

// Temporary: until geojson backend hydration includes CadentGas point enrichment (UK floods)
// Set to "1" once the backend provides this enrichment so the frontend can skip it.
const GEOJSON_BACKEND_INCLUDES_CADENT_POINT_ENRICHMENT =
  (process.env.REACT_APP_GEOJSON_BACKEND_INCLUDES_CADENT_POINT_ENRICHMENT ?? "0") === "1";
const DEFAULT_CUSTOM_GEOJSON_RENDER_CACHE_MAX_SIZE = MiB(48);
const DEFAULT_CUSTOM_GEOJSON_RENDER_CACHE_DELAY_EVICTION_SIZE = MiB(64);
const DEFAULT_CUSTOM_GEOJSON_GEOMETRY_CACHE_MAX_SIZE = MiB(64);
const DEFAULT_CUSTOM_GEOJSON_GEOMETRY_CACHE_DELAY_EVICTION_SIZE = MiB(96);
export const CUSTOM_GEOJSON_POLYGON_SERIES_MAX_TIMESTEPS = 100;
export const CUSTOM_GEOJSON_POLYGON_FEATURE_ID_PROPERTY = "__metx_custom_geojson_polygon_feature_id";

function createEmptySlicedCustomGeoJSON(): SlicedCustomGeoJSON {
  return {
    lineStrings: emptyFeatureCollection as FeatureCollection<LineString, GeoJsonProperties>,
    points: emptyFeatureCollection as FeatureCollection<Point, GeoJsonProperties>,
    polygons: emptyFeatureCollection as FeatureCollection<PolygonGeometry, GeoJsonProperties>,
  };
}

// Clone only feature wrappers/properties so render-cache entries can share immutable geometry arrays
// with the static geometry cache without reusing mutable properties objects.
function cloneFeatureCollectionWithSharedGeometry<T extends GeoJSON.Geometry>(
  collection: FeatureCollection<T, GeoJsonProperties>,
): FeatureCollection<T, GeoJsonProperties> {
  return {
    ...collection,
    features: collection.features.map((feature) => ({
      ...feature,
      properties: feature.properties ? { ...feature.properties } : feature.properties,
    })),
  };
}

function cloneSlicedCustomGeoJSON(data: SlicedCustomGeoJSON): SlicedCustomGeoJSON {
  return {
    points: cloneFeatureCollectionWithSharedGeometry(data.points),
    lineStrings: cloneFeatureCollectionWithSharedGeometry(data.lineStrings),
    polygons: cloneFeatureCollectionWithSharedGeometry(data.polygons),
  };
}

function staticGeometryCacheId(userFileId: number): string {
  return `static-sliced-geojson-${userFileId}`;
}

function polygonSeriesSourceDataKey(userFileId: number): string {
  return `polygon-series-source-${userFileId}`;
}

/**
 * Creates the neutral fallback value returned when a polygon-series chunk has not been requested
 * yet or when a request fails permanently.
 */
function createEmptyPolygonSeriesChunk(cacheId = "empty"): CustomGeoJSONPolygonSeriesChunk {
  return {
    cacheId,
    sourceDataKey: "empty",
    chunkIndex: -1,
    chunkFrameTimestamps: [],
    lineStrings: emptyFeatureCollection as FeatureCollection<LineString, GeoJsonProperties>,
    points: emptyFeatureCollection as FeatureCollection<Point, GeoJsonProperties>,
    polygons: emptyFeatureCollection as FeatureCollection<PolygonGeometry, GeoJsonProperties>,
    frameStatesByTimestampMs: {},
  };
}

/**
 * Converts a chunk's frame timestamps into the backend duration needed to request the whole chunk.
 * The request spans from the first frame to the last frame in the chunk.
 */
function polygonSeriesRequestDuration(chunkFrameTimestamps: DateTime[], temporalResolution: Duration): Duration {
  if (chunkFrameTimestamps.length <= 1) {
    return temporalResolution;
  }

  const firstFrame = chunkFrameTimestamps[0];
  const lastFrame = chunkFrameTimestamps[chunkFrameTimestamps.length - 1];
  return Duration.fromMillis(lastFrame.toMillis() - firstFrame.toMillis());
}

function dateTimeFromApiDate(date: string): DateTime | null {
  // setZone keeps explicit UTC offsets from the API response before converting to UTC.
  const parsed = DateTime.fromISO(date, { setZone: true }).toUTC();
  return parsed?.isValid ? parsed : null;
}

export interface GeoJSONCacheStats {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
  renderCount: number;
  renderPending: number;
  renderEstimatedMemoryConsumption: number;
  geometryCount: number;
  geometryPending: number;
  geometryEstimatedMemoryConsumption: number;
  polygonSeriesCount: number;
  polygonSeriesPending: number;
  polygonSeriesEstimatedMemoryConsumption: number;
}

export class CustomGeoJSONCache implements Stats<GeoJSONCacheStats> {
  // Final request-scoped output, including hydration/enrichment decisions for the specific request.
  protected cache: {
    [key: string]: CacheDatum<SlicedCustomGeoJSON, RetryState>;
  } = {};
  // Static sliced geometry keyed only by uploaded file id so identical uploads are not re-sliced per frame.
  protected geometryCache: {
    [key: string]: CacheDatum<SlicedCustomGeoJSON, RetryState>;
  } = {};
  protected polygonSeriesCache: {
    [key: string]: CacheDatum<CustomGeoJSONPolygonSeriesChunk, RetryState>;
  } = {};

  constructor(
    private readonly label_: string,
    private readonly maxSize: number = DEFAULT_CUSTOM_GEOJSON_RENDER_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_CUSTOM_GEOJSON_RENDER_CACHE_DELAY_EVICTION_SIZE,
    private readonly maxGeometrySize: number = DEFAULT_CUSTOM_GEOJSON_GEOMETRY_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilGeometrySize: number = DEFAULT_CUSTOM_GEOJSON_GEOMETRY_CACHE_DELAY_EVICTION_SIZE,
  ) {
    // Cache limits are configured through constructor parameters.
  }

  stats(): GeoJSONCacheStats {
    let renderCount = 0;
    let renderPending = 0;
    let geometryCount = 0;
    let geometryPending = 0;
    let polygonSeriesCount = 0;
    let polygonSeriesPending = 0;
    const renderEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.cache);
    const geometryEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.geometryCache);
    const polygonSeriesEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.polygonSeriesCache);

    for (const line in this.cache) {
      if (Object.hasOwn(this.cache, line)) {
        renderCount++;

        if (this.cache[line].state === CacheDatumState.Pending) {
          renderPending++;
        }
      }
    }

    for (const line in this.geometryCache) {
      if (Object.hasOwn(this.geometryCache, line)) {
        geometryCount++;

        if (this.geometryCache[line].state === CacheDatumState.Pending) {
          geometryPending++;
        }
      }
    }

    for (const line in this.polygonSeriesCache) {
      if (Object.hasOwn(this.polygonSeriesCache, line)) {
        polygonSeriesCount++;

        if (this.polygonSeriesCache[line].state === CacheDatumState.Pending) {
          polygonSeriesPending++;
        }
      }
    }

    return {
      count: renderCount + geometryCount + polygonSeriesCount,
      pending: renderPending + geometryPending + polygonSeriesPending,
      estimatedMemoryConsumption:
        renderEstimatedMemoryConsumption + geometryEstimatedMemoryConsumption + polygonSeriesEstimatedMemoryConsumption,
      renderCount,
      renderPending,
      renderEstimatedMemoryConsumption,
      geometryCount,
      geometryPending,
      geometryEstimatedMemoryConsumption,
      polygonSeriesCount,
      polygonSeriesPending,
      polygonSeriesEstimatedMemoryConsumption,
    };
  }

  descJsx(): JSX.Element {
    return (
      <>
        Special-purpose cache for custom GeoJSON requests. Keeps static sliced geometry separate from dynamic
        hydrated/rendered output, both with approximate memory-based LRU.
      </>
    );
  }

  clear() {
    this.cache = {};
    this.geometryCache = {};
    this.polygonSeriesCache = {};
  }

  label(): string {
    return this.label_;
  }

  private markUsed(cacheLine: LoadedCacheDatum<SlicedCustomGeoJSON>) {
    markCacheDatumUsed(cacheLine);
  }

  private markGeometryUsed(cacheLine: LoadedCacheDatum<SlicedCustomGeoJSON>) {
    markCacheDatumUsed(cacheLine);
  }

  private markPolygonSeriesUsed(cacheLine: LoadedCacheDatum<CustomGeoJSONPolygonSeriesChunk>) {
    markCacheDatumUsed(cacheLine);
  }

  private unloadCacheEntry(_: CacheDatum<SlicedCustomGeoJSON, RetryState> | undefined, cacheId: string) {
    delete this.cache[cacheId];
  }

  private unloadGeometryCacheEntry(_: CacheDatum<SlicedCustomGeoJSON, RetryState> | undefined, cacheId: string) {
    delete this.geometryCache[cacheId];
  }

  private unloadPolygonSeriesCacheEntry(
    _: CacheDatum<CustomGeoJSONPolygonSeriesChunk, RetryState> | undefined,
    cacheId: string,
  ) {
    delete this.polygonSeriesCache[cacheId];
  }

  private pruneCacheEntries() {
    evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, this.unloadCacheEntry.bind(this));
  }

  private pruneGeometryCacheEntries() {
    evictCacheEntriesBySize(
      this.geometryCache,
      this.maxGeometrySize,
      this.delayEvictionUntilGeometrySize,
      this.unloadGeometryCacheEntry.bind(this),
    );
  }

  private prunePolygonSeriesCacheEntries() {
    evictCacheEntriesBySize(
      this.polygonSeriesCache,
      this.maxSize,
      this.delayEvictionUntilSize,
      this.unloadPolygonSeriesCacheEntry.bind(this),
    );
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  /**
   * A method to return a cached data from cache.
   * It does not trigger a new request when the given request hasn't been emitted.
   * @param cacheId
   * @returns
   */
  peekCache(cacheId: string): AsyncResult<SlicedCustomGeoJSON> {
    const v = this.cache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult(createEmptySlicedCustomGeoJSON());
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise);
        }
        // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
        // mark it as such to prevent concurrent invocations from reentering the remaining function body
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult(createEmptySlicedCustomGeoJSON());
      case CacheDatumState.Loaded:
        this.markUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  private peekGeometryCache(cacheId: string): AsyncResult<SlicedCustomGeoJSON> {
    const v = this.geometryCache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult(createEmptySlicedCustomGeoJSON());
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise);
        }
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult(createEmptySlicedCustomGeoJSON());
      case CacheDatumState.Loaded:
        this.markGeometryUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  private peekPolygonSeriesCache(cacheId: string): AsyncResult<CustomGeoJSONPolygonSeriesChunk> {
    const v = this.polygonSeriesCache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult(createEmptyPolygonSeriesChunk(cacheId));
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise);
        }
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult(createEmptyPolygonSeriesChunk(cacheId));
      case CacheDatumState.Loaded:
        this.markPolygonSeriesUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  // Fetch and slice the uploaded GeoJSON once per file id. Dynamic requests clone from this cache
  // unless backend line hydration has to generate request-specific geometry on the server.
  private retrieveStaticSlicedGeoJSON(userFileId: number): AsyncResult<SlicedCustomGeoJSON> {
    const cacheId = staticGeometryCacheId(userFileId);
    if (Object.hasOwn(this.geometryCache, cacheId)) {
      return this.peekGeometryCache(cacheId);
    }

    const retry = createRetryState();
    const promise = networkCaches.generic_cache
      .retrieve<any, any, GeoJSON>([QueryKey.CUSTOM_GEOJSON_FILE, userFileId], () => Api.file.getFile({ userFileId }))
      .then(
        (geoJson) => dataProcessingThreadPool.run("sliceGeoJSONByFeatures", geoJson) as Promise<SlicedCustomGeoJSON>,
      )
      .then((data) => {
        const pendingEntry = this.geometryCache[cacheId];
        const requestDatetime = pendingEntry?.requestDatetime ?? performance.now();
        const cacheData: LoadedCacheDatum<SlicedCustomGeoJSON> = createLoadedCacheDatum(data, requestDatetime);
        this.markGeometryUsed(cacheData);
        this.geometryCache[cacheId] = cacheData;
        this.pruneGeometryCacheEntries();
        return data;
      })
      .catch((e) => {
        this.geometryCache[cacheId] = {
          requestDatetime: performance.now(),
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };

        return Promise.reject(e);
      });

    this.geometryCache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise,
      payload: retry,
    };

    return createPendingAsyncResult(promise);
  }

  /**
   * Resolves the frame at `datetime` into the chunk that owns it inside the animation window.
   * If the exact frame is absent because the caller uses a rounded/snapped timestamp, the nearest
   * earlier frame is used so the correct chunk can still be reused.
   */
  private resolvePolygonSeriesChunk(
    customGeoJsonRequestOption: CustomGeoJsonRequestOption,
    animationContext: CustomGeoJSONAnimationFrameContext,
    datetime: DateTime,
  ) {
    const frameTimestamps = animationContext.frameTimestamps;
    if (!frameTimestamps.length) {
      return null;
    }

    const datetimeMs = datetime.toMillis();
    let frameIndex = frameTimestamps.findIndex((frameTimestamp) => frameTimestamp.toMillis() === datetimeMs);
    if (frameIndex < 0) {
      frameIndex = 0;
      for (let index = frameTimestamps.length - 1; index >= 0; index -= 1) {
        if (frameTimestamps[index].toMillis() <= datetimeMs) {
          frameIndex = index;
          break;
        }
      }
    }

    const chunkIndex = Math.floor(frameIndex / CUSTOM_GEOJSON_POLYGON_SERIES_MAX_TIMESTEPS);
    const chunkStartIndex = chunkIndex * CUSTOM_GEOJSON_POLYGON_SERIES_MAX_TIMESTEPS;
    const chunkFrameTimestamps = frameTimestamps.slice(
      chunkStartIndex,
      chunkStartIndex + CUSTOM_GEOJSON_POLYGON_SERIES_MAX_TIMESTEPS,
    );
    const chunkStart = chunkFrameTimestamps[0];
    if (!chunkStart) {
      return null;
    }

    const { user_file_id, model, parameter_unit, customOption, thresholds, advancedThresholds } =
      customGeoJsonRequestOption;
    const cacheId = constructPolygonSeriesChunkHashId({
      userFileId: user_file_id,
      animationWindowKey: animationContext.windowKey,
      chunkIndex,
      chunkStartMs: chunkStart.toMillis(),
      temporalResolutionMs: animationContext.temporalResolution.toMillis(),
      chunkFrameCount: chunkFrameTimestamps.length,
      model,
      parameter: parameter_unit,
      customOption,
      advancedThresholds,
      hasBasicThresholds: !!thresholds?.length,
    });

    return {
      cacheId,
      chunkIndex,
      chunkFrameTimestamps,
    };
  }

  /**
   * Non-fetching cache lookup used by buffering checks to see whether the polygon-series chunk for
   * the current animation frame is pending or already materialized.
   */
  peekCustomGeoJSONPolygonSeriesChunk(
    customGeoJsonRequestOption: CustomGeoJsonRequestOption,
    animationContext: CustomGeoJSONAnimationFrameContext,
    datetime: DateTime,
  ): AsyncResult<CustomGeoJSONPolygonSeriesChunk> {
    const chunk = this.resolvePolygonSeriesChunk(customGeoJsonRequestOption, animationContext, datetime);
    if (!chunk) {
      return createNotRequestedAsyncResult(createEmptyPolygonSeriesChunk());
    }
    return this.peekPolygonSeriesCache(chunk.cacheId);
  }

  /**
   * Loads or joins the polygon-series chunk for the animation window containing `datetime`.
   * Each chunk keeps static geometry plus a per-frame value map so animation can reuse geometry
   * while swapping only polygon properties that vary with time.
   */
  retrieveCustomGeoJSONPolygonSeriesChunk(
    customGeoJsonRequestOption: CustomGeoJsonRequestOption,
    animationContext: CustomGeoJSONAnimationFrameContext,
    datetime: DateTime,
  ): AsyncResult<CustomGeoJSONPolygonSeriesChunk> {
    const chunk = this.resolvePolygonSeriesChunk(customGeoJsonRequestOption, animationContext, datetime);
    if (!chunk) {
      return createResolvedAsyncResult(createEmptyPolygonSeriesChunk());
    }

    if (Object.hasOwn(this.polygonSeriesCache, chunk.cacheId)) {
      return this.peekPolygonSeriesCache(chunk.cacheId);
    }

    const retry = createRetryState();
    const promise = this.retrieveStaticSlicedGeoJSON(customGeoJsonRequestOption.user_file_id)
      .asynchronous.then((data) => {
        const cloned = cloneSlicedCustomGeoJSON(data);
        const polygons = this.createPolygonSeriesSourceData(customGeoJsonRequestOption.user_file_id, cloned.polygons);
        const valueKeys = this.getPolygonSeriesValueKeys(customGeoJsonRequestOption);
        const frameStatesByTimestampMs = this.createEmptyPolygonSeriesFrameStates(
          chunk.chunkFrameTimestamps,
          polygons,
          valueKeys,
        );

        if (!valueKeys.length) {
          return {
            cacheId: chunk.cacheId,
            sourceDataKey: polygonSeriesSourceDataKey(customGeoJsonRequestOption.user_file_id),
            chunkIndex: chunk.chunkIndex,
            chunkFrameTimestamps: chunk.chunkFrameTimestamps,
            lineStrings: cloned.lineStrings,
            points: cloned.points,
            polygons,
            frameStatesByTimestampMs,
          } satisfies CustomGeoJSONPolygonSeriesChunk;
        }

        return this.hydratePolygonSeriesFrameStates(
          polygons,
          frameStatesByTimestampMs,
          chunk.chunkFrameTimestamps,
          animationContext.temporalResolution,
          customGeoJsonRequestOption,
        ).then(
          () =>
            ({
              cacheId: chunk.cacheId,
              sourceDataKey: polygonSeriesSourceDataKey(customGeoJsonRequestOption.user_file_id),
              chunkIndex: chunk.chunkIndex,
              chunkFrameTimestamps: chunk.chunkFrameTimestamps,
              lineStrings: cloned.lineStrings,
              points: cloned.points,
              polygons,
              frameStatesByTimestampMs,
            }) satisfies CustomGeoJSONPolygonSeriesChunk,
        );
      })
      .then((data) => {
        const pendingEntry = this.polygonSeriesCache[chunk.cacheId];
        const requestDatetime = pendingEntry?.requestDatetime ?? performance.now();
        const cacheData: LoadedCacheDatum<CustomGeoJSONPolygonSeriesChunk> = createLoadedCacheDatum(
          data,
          requestDatetime,
        );
        this.markPolygonSeriesUsed(cacheData);
        this.polygonSeriesCache[chunk.cacheId] = cacheData;
        this.prunePolygonSeriesCacheEntries();
        return data;
      })
      .catch((e) => {
        this.polygonSeriesCache[chunk.cacheId] = {
          requestDatetime: performance.now(),
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };

        return Promise.reject(e);
      });

    this.polygonSeriesCache[chunk.cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise,
      payload: retry,
    };
    return createPendingAsyncResult(promise);
  }

  /**
   * Adds stable ids to polygon features so chunked time-series values can later be mapped back onto
   * the correct polygon for a given animation frame.
   */
  private createPolygonSeriesSourceData(
    userFileId: number,
    polygons: FeatureCollection<PolygonGeometry, GeoJsonProperties>,
  ): FeatureCollection<PolygonGeometry, GeoJsonProperties> {
    return {
      ...polygons,
      features: polygons.features.map((feature, index) => {
        const featureId = `${userFileId}:polygon:${index}`;
        return {
          ...feature,
          id: featureId,
          properties: {
            ...feature.properties,
            [CUSTOM_GEOJSON_POLYGON_FEATURE_ID_PROPERTY]: featureId,
          },
        };
      }),
    };
  }

  private getPolygonSeriesValueKeys(customGeoJsonRequestOption: CustomGeoJsonRequestOption): string[] {
    const advancedDetails = customGeoJsonRequestOption.advancedThresholds
      ?.flatMap((threshold) => threshold.details ?? [])
      .filter((detail) => detail.parameter && detail.model);

    if (advancedDetails?.length) {
      return advancedDetails.map((detail) => advancedThresholdValuePropertyName(detail.id));
    }

    if (
      customGeoJsonRequestOption.thresholds?.length &&
      customGeoJsonRequestOption.model &&
      customGeoJsonRequestOption.parameter_unit
    ) {
      return ["value"];
    }

    return [];
  }

  /**
   * Pre-allocates the per-frame polygon state map for a chunk.
   * Every polygon/value-key pair starts as `null` so partial backend responses still produce a
   * stable render shape.
   */
  private createEmptyPolygonSeriesFrameStates(
    chunkFrameTimestamps: DateTime[],
    polygons: FeatureCollection<PolygonGeometry, GeoJsonProperties>,
    valueKeys: string[],
  ): Record<string, Record<string, CustomGeoJSONPolygonFeatureState>> {
    const featureIds = polygons.features.flatMap((feature) => {
      const featureId = feature.properties?.[CUSTOM_GEOJSON_POLYGON_FEATURE_ID_PROPERTY];
      return typeof featureId === "string" || typeof featureId === "number" ? [String(featureId)] : [];
    });

    return Object.fromEntries(
      chunkFrameTimestamps.map((frameTimestamp) => [
        String(frameTimestamp.toMillis()),
        Object.fromEntries(
          featureIds.map((featureId) => [featureId, Object.fromEntries(valueKeys.map((valueKey) => [valueKey, null]))]),
        ),
      ]),
    );
  }

  /**
   * Fills the pre-allocated frame-state map by fetching polygon time series from the polygon cache.
   * Advanced thresholds fan out one request per threshold detail; basic thresholds use one request
   * per polygon.
   */
  private hydratePolygonSeriesFrameStates(
    polygons: FeatureCollection<PolygonGeometry, GeoJsonProperties>,
    frameStatesByTimestampMs: Record<string, Record<string, CustomGeoJSONPolygonFeatureState>>,
    chunkFrameTimestamps: DateTime[],
    temporalResolution: Duration,
    customGeoJsonRequestOption: CustomGeoJsonRequestOption,
  ): Promise<void> {
    if (!chunkFrameTimestamps.length || !polygons.features.length) {
      return Promise.resolve();
    }

    const advancedDetails = customGeoJsonRequestOption.advancedThresholds
      ?.flatMap((threshold) => threshold.details ?? [])
      .filter((detail) => detail.parameter && detail.model);

    if (advancedDetails?.length) {
      const promises = polygons.features.flatMap((feature) =>
        advancedDetails.map((detail) => {
          const offsetMinutes = detail.offsetMinutes ?? 0;
          return networkCaches.polygonJson_cache
            .retrievePolygon(
              this.createPolygonSeriesRequest(
                feature,
                detail.parameter,
                detail.model,
                chunkFrameTimestamps[0].plus({ minutes: offsetMinutes }),
                polygonSeriesRequestDuration(chunkFrameTimestamps, temporalResolution),
                temporalResolution,
                detail.aggregate ?? Aggregate.max,
              ),
            )
            .asynchronous.then((data) => {
              this.applyPolygonSeriesResponseValues({
                data,
                feature,
                frameStatesByTimestampMs,
                propertyName: advancedThresholdValuePropertyName(detail.id),
                offsetMinutes,
              });
            });
        }),
      );
      return Promise.allSettled(promises).then(() => undefined);
    }

    const { model, parameter_unit, customOption } = customGeoJsonRequestOption;
    if (!model || !parameter_unit) {
      return Promise.resolve();
    }

    const aggregate = toEnumValue<Aggregate>(customOption.aggregate_value || "", enumValues(Aggregate), Aggregate.max);
    const promises = polygons.features.map((feature) =>
      networkCaches.polygonJson_cache
        .retrievePolygon(
          this.createPolygonSeriesRequest(
            feature,
            parameter_unit,
            model,
            chunkFrameTimestamps[0],
            polygonSeriesRequestDuration(chunkFrameTimestamps, temporalResolution),
            temporalResolution,
            aggregate,
          ),
        )
        .asynchronous.then((data) => {
          this.applyPolygonSeriesResponseValues({
            data,
            feature,
            frameStatesByTimestampMs,
            propertyName: "value",
            offsetMinutes: 0,
          });
        }),
    );

    return Promise.allSettled(promises).then(() => undefined);
  }

  /**
   * Builds the backend polygon request for a single polygon/value stream inside a chunk.
   */
  private createPolygonSeriesRequest(
    feature: Feature<PolygonGeometry, GeoJsonProperties>,
    parameter: string,
    model: string,
    startDatetime: DateTime,
    duration: Duration,
    temporalResolution: Duration,
    aggregate: Aggregate,
  ): PolygonRequest<CoordinateSystem.WGS84> {
    return {
      feature,
      aggregate,
      startDatetime,
      duration,
      temporalResolution,
      width: 0,
      height: 0,
      parameters: [parameter],
      model,
      boundingBoxLimit: null,
    };
  }

  /**
   * Writes backend polygon-series values back into the frame-state map.
   * Offset-based advanced thresholds are normalized to the base animation frame timestamp so the
   * render path can always index by displayed frame time.
   */
  private applyPolygonSeriesResponseValues(args: {
    data: Array<Array<{ dates?: Array<{ date: string; value: unknown }> }>>;
    feature: Feature<PolygonGeometry, GeoJsonProperties>;
    frameStatesByTimestampMs: Record<string, Record<string, CustomGeoJSONPolygonFeatureState>>;
    propertyName: string;
    offsetMinutes: number;
  }) {
    const featureId = args.feature.properties?.[CUSTOM_GEOJSON_POLYGON_FEATURE_ID_PROPERTY];
    if (featureId == null) {
      return;
    }

    const dates = args.data[0]?.[0]?.dates ?? [];
    for (const dateValue of dates) {
      const apiDateTime = dateTimeFromApiDate(dateValue.date);
      if (!apiDateTime) {
        continue;
      }

      const baseFrameTimestampMs = apiDateTime.minus({ minutes: args.offsetMinutes }).toMillis();
      const frameStates = args.frameStatesByTimestampMs[String(baseFrameTimestampMs)];
      const featureState = frameStates?.[String(featureId)];
      if (!featureState) {
        continue;
      }

      featureState[args.propertyName] = dateValue.value ?? null;
    }
  }

  /**
   * Materializes one animation frame from a polygon-series chunk by copying that frame's polygon
   * values onto the sliced GeoJSON returned by the main cache entrypoint.
   */
  private applyPolygonSeriesFrameToSlicedGeoJSON(
    data: SlicedCustomGeoJSON,
    polygonSeriesChunk: CustomGeoJSONPolygonSeriesChunk,
    datetime: DateTime,
    userFileId: number,
  ): SlicedCustomGeoJSON {
    const statesByFeatureId = polygonSeriesChunk.frameStatesByTimestampMs[String(datetime.toMillis())];
    if (!statesByFeatureId) {
      return data;
    }

    const polygons = this.createPolygonSeriesSourceData(userFileId, data.polygons);
    return {
      ...data,
      polygons: {
        ...polygons,
        features: polygons.features.map((feature) => {
          const featureId = feature.properties?.[CUSTOM_GEOJSON_POLYGON_FEATURE_ID_PROPERTY];
          const state = featureId == null ? null : statesByFeatureId[String(featureId)];
          if (!state) {
            return feature;
          }

          return {
            ...feature,
            properties: {
              ...feature.properties,
              ...state,
            },
          };
        }),
      },
    };
  }

  /**
   * Main request-scoped cache entrypoint for custom GeoJSON.
   * This orchestrates geometry loading, optional backend line hydration, legacy frontend hydration,
   * animation-specific polygon-series hydration, Cadent point enrichment, and extra-point creation.
   */
  retrieveCustomGeoJSON(
    customGeoJsonRequestOption: CustomGeoJsonRequestOption,
    datetime?: DateTime,
    animationContext?: CustomGeoJSONAnimationFrameContext | null,
  ): AsyncResult<SlicedCustomGeoJSON> {
    const { user_file_id, model, parameter_unit, customOption, thresholds, advancedThresholds } =
      customGeoJsonRequestOption;

    const backendHydrateMode = isGeojsonBackendHydrationEnabled();

    const hashId = constructHashId(
      user_file_id,
      datetime,
      model,
      parameter_unit,
      customOption,
      advancedThresholds,
      !!thresholds?.length,
      backendHydrateMode,
    );
    if (Object.hasOwn(this.cache, hashId)) {
      return this.peekCache(hashId);
    }

    const retry_: RetryState | null = null;
    const retry = retry_ ?? createRetryState();

    const hasThresholds = !!thresholds?.length || !!advancedThresholds?.length;
    const shouldHydrateValues = hasThresholds || !!customOption.show_values;

    // Build backend hydration inputs only for requests that actually vary with time/parameter settings.
    const buildHydrationParameters = (): HydrationParameterIn[] => {
      if (!datetime) {
        return [];
      }

      const advancedDetails = customGeoJsonRequestOption.advancedThresholds
        ?.flatMap((t) => t.details ?? [])
        .filter((d) => d.parameter && d.model);

      if (advancedDetails?.length) {
        return advancedDetails.map((d) => ({
          aggregate_value: d.aggregate,
          model: d.model,
          parameter_unit: d.parameter,
          when: datetime
            .plus(Duration.fromObject({ minutes: d.offsetMinutes }))
            .toUTC()
            .toISO(),
          parameter_id: advancedThresholdValuePropertyName(d.id),
        }));
      }

      const { model, parameter_unit, customOption } = customGeoJsonRequestOption;
      if (!model || !parameter_unit) {
        return [];
      }

      return [
        {
          aggregate_value: customOption.aggregate_value ?? undefined,
          model,
          parameter_unit,
          when: datetime.toUTC().toISO(),
        },
      ];
    };

    const hydrationParameters = buildHydrationParameters();
    const canHydrate = !!datetime && hydrationParameters.length > 0;
    const didBackendHydrateLines = backendHydrateMode && hydrationParameters.length > 0;
    const didFrontendHydrate = shouldHydrateValues && canHydrate;
    let telemetryAttributes: Record<string, string | number | boolean> | undefined;
    const perfOp = "load";
    const perfMode = didBackendHydrateLines ? ("backend" as const) : ("frontend" as const);
    const t0 = performance.now();
    let backendRequestMs: number | null = null;
    let tSliceDone: number | null = null;
    let tHydrateDone: number | null = null;
    let tDone: number | null = null;

    const perfRun = createPerfRun({
      op: perfOp,
      mode: perfMode,
      meta: {
        cacheId: hashId,
        userFileId: user_file_id,
        hasThresholds,
        canHydrate,
        didBackendHydrateLines,
        didFrontendHydrate,
        backendHydrateEnabled: backendHydrateMode,
        backendIncludesCadent: GEOJSON_BACKEND_INCLUDES_CADENT_POINT_ENRICHMENT,
      },
    });

    // Backend polygon hydration is intentionally disabled. Keep backend line hydration,
    // but always resolve polygon thresholds on the frontend or via polygon-series chunks.
    const hydrateViaBackendIfAvailable = () => {
      const units = customOption.units || customGeoJsonLayerConfig.defaultLineSegmentUnit;
      const segmentLength = customOption.segmentLength || customGeoJsonLayerConfig.defaultLineSegmentLength;
      const fetchStarted = performance.now();

      return Api.geojson
        .geojsonHydrate({
          fileId: user_file_id,
          geojsonHydrateRequestIn: {
            parameters: hydrationParameters,
            hydrate_points: false,
            hydrate_polygons: false,
          },
          units: units,
          segmentLength: segmentLength,
        })
        .then((geoJson) => {
          backendRequestMs = performance.now() - fetchStarted;
          recordLayerFetchMs({
            durationMs: backendRequestMs,
            source: "custom_geojson",
            op: perfOp,
            mode: perfMode,
            attributes: telemetryAttributes,
          });
          perfRun.mark("tFetchDone");
          return dataProcessingThreadPool.run("sliceGeoJSONByFeatures", geoJson) as Promise<SlicedCustomGeoJSON>;
        })
        .then((data) => {
          tSliceDone = performance.now();
          perfRun.mark("tSliceDone");
          return data;
        });
    };

    // Clone the static geometry cache entry before frontend hydration mutates feature properties.
    const loadStaticSlicedGeoJSON = () =>
      this.retrieveStaticSlicedGeoJSON(user_file_id).asynchronous.then((data) => {
        perfRun.mark("tFetchDone");
        tSliceDone = performance.now();
        perfRun.mark("tSliceDone");
        return cloneSlicedCustomGeoJSON(data);
      });

    // Use request-specific backend geometry only when backend line hydration is active.
    // Polygon hydration stays on the frontend even in backend line-hydration mode.
    const baseGeoJsonPromise = didBackendHydrateLines ? hydrateViaBackendIfAvailable() : loadStaticSlicedGeoJSON();

    const promise = baseGeoJsonPromise
      .then((data) => {
        const nPoints = data.points.features.length;
        const nPolygons = data.polygons.features.length;
        const nLines = data.lineStrings.features.length;
        perfRun.addMeta({
          nPoints,
          nPolygons,
          nLines,
        });
        recordCustomGeojsonCounts({
          nPoints,
          nPolygons,
          nLines,
          mode: perfMode,
          attributes: telemetryAttributes,
        });

        const shouldUsePolygonSeriesHydration = !!animationContext && hasThresholds && canHydrate && !!datetime;

        if (!shouldHydrateValues || !canHydrate || !datetime) {
          tHydrateDone = performance.now();
          perfRun.mark("tHydrateDone");
          return data;
        }

        // LEGACY in-browser hydration (kept for Points/Polygons; and for Lines when backend hydration isn't used).
        return processCustomGeoJson(
          data,
          retry,
          customGeoJsonRequestOption,
          datetime,
          parameter_unit ?? "",
          model ?? "",
          {
            hydrateLines: !didBackendHydrateLines,
            hydratePolygons: !shouldUsePolygonSeriesHydration,
          },
        ).then(async (hydrated) => {
          if (!shouldUsePolygonSeriesHydration || !datetime) {
            tHydrateDone = performance.now();
            perfRun.mark("tHydrateDone");
            return hydrated;
          }

          const polygonSeriesChunk = await this.retrieveCustomGeoJSONPolygonSeriesChunk(
            customGeoJsonRequestOption,
            animationContext,
            datetime,
          ).asynchronous;

          tHydrateDone = performance.now();
          perfRun.mark("tHydrateDone");
          return this.applyPolygonSeriesFrameToSlicedGeoJSON(
            hydrated,
            polygonSeriesChunk,
            datetime,
            customGeoJsonRequestOption.user_file_id,
          );
        });
      })
      .then(async (data) => {
        if (didBackendHydrateLines && GEOJSON_BACKEND_INCLUDES_CADENT_POINT_ENRICHMENT) {
          perfRun.mark("tCadentDone");
          return data;
        }

        // Only run Cadent flood enrichment for ta_code points.
        if (!hasTaCodePoints(data.points.features)) {
          perfRun.mark("tCadentDone");
          return data;
        }

        // CadentGas flood enrichment is expensive; only run it when ta_code points exist.
        const hydratedPointParts = await Promise.all(hydratePointFeatures(data.points.features));
        const hydratedPoints = hydratedPointParts.flat() as Array<Feature<Point, GeoJsonProperties>>;

        perfRun.mark("tCadentDone");
        return {
          ...data,
          points: {
            type: "FeatureCollection" as const,
            features: hydratedPoints,
          },
        } satisfies SlicedCustomGeoJSON;
      })
      .then((data) => {
        const extraPoints = createPointByPolygonProperty(data.polygons.features);
        perfRun.addMeta({ nExtraPoints: extraPoints.length });

        if (extraPoints.length === 0) {
          perfRun.mark("tExtraPointsDone");
          return data;
        }

        const updated = {
          ...data,
          points: {
            type: "FeatureCollection" as const,
            features: [...data.points.features, ...extraPoints],
          },
        } satisfies SlicedCustomGeoJSON;

        perfRun.mark("tExtraPointsDone");
        return updated;
      })
      .then((data) => {
        tDone = performance.now();
        perfRun.mark("tDone");
        const frontendHydrationMs =
          didFrontendHydrate && tSliceDone != null && tHydrateDone != null ? tHydrateDone - tSliceDone : null;
        const totalMs = tDone - t0;

        perfRun.finish([
          { phase: "fetch", startMark: "t0", endMark: "tFetchDone" },
          { phase: "slice", startMark: "tFetchDone", endMark: "tSliceDone" },
          // Post-slice work in the frontend. In backend mode (or when hydration is skipped),
          // we mark tHydrateDone immediately after slicing so this phase is ~0ms.
          { phase: "hydrate", startMark: "tSliceDone", endMark: "tHydrateDone" },
          { phase: "cadent", startMark: "tHydrateDone", endMark: "tCadentDone" },
          { phase: "extraPoints", startMark: "tCadentDone", endMark: "tExtraPointsDone" },
          { phase: "total", startMark: "t0", endMark: "tDone" },
        ]);
        if (tSliceDone != null && tHydrateDone != null) {
          recordCustomGeojsonHydrateMs({
            durationMs: tHydrateDone - tSliceDone,
            op: perfOp,
            mode: perfMode,
            didFrontendHydrate,
            attributes: telemetryAttributes,
          });
        }
        if (tDone != null) {
          recordCustomGeojsonTotalMs({
            durationMs: totalMs,
            op: perfOp,
            mode: perfMode,
            didFrontendHydrate,
            attributes: telemetryAttributes,
          });
        }
        perfRun.clear();

        const pendingEntry = this.cache[hashId];
        const requestDatetime = pendingEntry?.requestDatetime ?? performance.now();
        const cacheData: LoadedCacheDatum<SlicedCustomGeoJSON> = createLoadedCacheDatum(data, requestDatetime);
        this.markUsed(cacheData);
        this.cache[hashId] = cacheData;
        this.pruneCacheEntries();
        return data;
      })
      .catch((e) => {
        // No perfLog("error", ...) anymore
        this.cache[hashId] = {
          requestDatetime: Date.now(),
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };

        return Promise.reject(e);
      });

    this.cache[hashId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise,
      payload: retry,
    };
    return createPendingAsyncResult(promise);
  }
}
